import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import { FaFacebook, FaInstagram, FaTwitter, FaYoutube, FaHeart } from 'react-icons/fa';
import { Link } from 'react-scroll';

const FooterContainer = styled.footer`
  background: ${theme.colors.dark};
  color: ${theme.colors.textLight};
  padding: 60px 0 20px;
`;

const FooterWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  grid-gap: 30px;
  margin-bottom: 50px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div`
  text-align: ${props => props.centered ? 'center' : 'left'};
`;

const FooterLogo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  
  img {
    @media screen and (max-width: 480px) {
      height: 150px !important;
    }
  }
`;

const LogoText = styled.h3`
  font-size: 2rem;
  color: ${theme.colors.textLight};
  margin-top: 5px;
  text-align: center;
  
  @media screen and (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const FooterDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 20px;
  text-align: inherit;
`;

const FooterHeading = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 10px;
  
  &:after {
    content: '';
    position: absolute;
    width: 40px;
    height: 2px;
    background-color: ${theme.colors.accent};
    bottom: 0;
    left: 0;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 10px;
`;

const StyledLink = styled(Link)`
  color: ${theme.colors.textLight};
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${theme.colors.accent};
  }
`;

const SocialIcons = styled.div`
  display: flex;
  margin-top: 20px;
  justify-content: ${props => props.centered ? 'center' : 'flex-start'};
`;

const SocialIcon = styled.a`
  color: ${theme.colors.textLight};
  font-size: 1.5rem;
  margin-right: 20px;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${theme.colors.accent};
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
`;

const CopyrightText = styled.p`
  margin-bottom: 10px;
`;

const MadeWith = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
`;

const HeartIcon = styled(FaHeart)`
  color: ${theme.colors.accent};
  margin: 0 5px;
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterWrapper>
        <FooterGrid>
          <FooterColumn centered>
            <FooterLogo>
              <img 
                src={`${process.env.PUBLIC_URL}/logo.png`} 
                alt="Rewilding Therapies Logo" 
                style={{ 
                  height: '202.5px', 
                  width: 'auto',
                  marginBottom: '15px', 
                  display: 'block'
                }} 
              />
              <LogoText>Rewilding Therapies</LogoText>
            </FooterLogo>
            <FooterDescription>
              Nature-based psychotherapy and movement coaching for individuals, groups, schools, and organisations.
            </FooterDescription>
            <SocialIcons centered>
              <SocialIcon href="#" target="_blank" aria-label="Facebook">
                <FaFacebook />
              </SocialIcon>
              <SocialIcon href="#" target="_blank" aria-label="Instagram">
                <FaInstagram />
              </SocialIcon>
              <SocialIcon href="#" target="_blank" aria-label="Twitter">
                <FaTwitter />
              </SocialIcon>
              <SocialIcon href="#" target="_blank" aria-label="YouTube">
                <FaYoutube />
              </SocialIcon>
            </SocialIcons>
          </FooterColumn>
          
          <FooterColumn>
            <FooterHeading>Quick Links</FooterHeading>
            <FooterLinks>
              <FooterLink>
                <StyledLink to="home" smooth={true} duration={500} spy={true} exact="true" offset={-80}>
                  Home
                </StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink to="about" smooth={true} duration={500} spy={true} exact="true" offset={-80}>
                  About
                </StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink to="services" smooth={true} duration={500} spy={true} exact="true" offset={-80}>
                  Services
                </StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink to="about-me" smooth={true} duration={500} spy={true} exact="true" offset={-80}>
                  About Me
                </StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink to="contact" smooth={true} duration={500} spy={true} exact="true" offset={-80}>
                  Contact
                </StyledLink>
              </FooterLink>
            </FooterLinks>
          </FooterColumn>
          
          <FooterColumn>
            <FooterHeading>Services</FooterHeading>
            <FooterLinks>
              <FooterLink>
                <StyledLink 
                  to="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.navigateTo) window.navigateTo('wilderness-therapy');
                  }}
                >
                  1:1 Wilderness Psychotherapy
                </StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink 
                  to="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.navigateTo) window.navigateTo('group-wilderness-therapy');
                  }}
                >
                  Group Wilderness Therapy
                </StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink 
                  to="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.navigateTo) window.navigateTo('movement-coaching');
                  }}
                >
                  WildStrong Movement Coaching
                </StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink to="services" smooth={true} duration={500} spy={true} exact="true" offset={-80}>
                  Retreats and Journeys
                </StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink 
                  to="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.navigateTo) window.navigateTo('movement-coaching');
                  }}
                >
                  Movement Coaching
                </StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink 
                  to="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.navigateTo) window.navigateTo('corporate-wellbeing');
                  }}
                >
                  Corporate Wellbeing
                </StyledLink>
              </FooterLink>
            </FooterLinks>
          </FooterColumn>
          
          <FooterColumn>
            <FooterHeading>Contact</FooterHeading>
            <FooterDescription>
              Email: hello@rewidingtherapies.co.uk
              <br />
              Phone: 07748747530
            </FooterDescription>
          </FooterColumn>
        </FooterGrid>
        
        <Copyright>
          <CopyrightText>
            © {new Date().getFullYear()} Rewilding Therapies. All rights reserved.
          </CopyrightText>
          <MadeWith>
            Made with <HeartIcon /> for nature and wellbeing
          </MadeWith>
        </Copyright>
      </FooterWrapper>
    </FooterContainer>
  );
};

export default Footer;